import { removeTrapFocus } from '@theme/focus';

/**
 * Custom (Miss Sophie): menu drawer below the header's top row.
 * Always on mobile. On desktop when snippets/custom-drawer-below-header.liquid sets
 * --ms-below-header-desktop: 1 on the drawer (Menu block > "Open below the header on desktop").
 *
 * Measures the bottom edge of the header's top row when the drawer opens and stores it as
 * --ms-drawer-top on the drawer's <details>. The CSS in snippets/custom-drawer-below-header.liquid
 * positions the drawer and its backdrop from there. Measuring at open time covers the announcement
 * bar above the header in every scroll state; the page is scroll-locked while the drawer is open.
 * [data-ms-below-header] is only set while this layout applies, so the CSS needs no breakpoints.
 *
 * Also, while the drawer is open below the header:
 * - Clicking the header's menu (close) icon closes the drawer with Horizon's animation
 * - Clicking another header control (logo, search, account, cart, desktop menu) closes the drawer first,
 *   so its focus trap doesn't pull focus back from the dialog that opens next
 *
 * Horizon's header-drawer.js is unchanged. Loaded by snippets/custom-drawer-below-header.liquid.
 */

const mobileQuery = window.matchMedia('(max-width: 749px)');

/**
 * @returns {HTMLDetailsElement | null} The menu drawer's details element
 */
function getDetails() {
  const details = document.getElementById('Details-menu-drawer-container');

  return details instanceof HTMLDetailsElement ? details : null;
}

/**
 * Whether the drawer opens below the header at the current width
 * @param {HTMLDetailsElement} details
 * @returns {boolean}
 */
function opensBelowHeader(details) {
  if (mobileQuery.matches) return true;

  return getComputedStyle(details).getPropertyValue('--ms-below-header-desktop').trim() === '1';
}

/**
 * Positions the drawer at the bottom edge of the header's top row,
 * or hands it back to Horizon's layout where the setting doesn't apply
 * @param {HTMLDetailsElement} details
 */
function placeDrawer(details) {
  if (!opensBelowHeader(details)) {
    details.removeAttribute('data-ms-below-header');
    return;
  }

  const topRow = details.closest('#header-component')?.querySelector('.header__row--top');

  if (!topRow) return;

  const bottom = Math.max(0, Math.round(topRow.getBoundingClientRect().bottom));

  details.style.setProperty('--ms-drawer-top', `${bottom}px`);
  details.setAttribute('data-ms-below-header', '');
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const details = getDetails();

    if (!details || !opensBelowHeader(details)) return;

    const summary = details.querySelector(':scope > summary');

    if (summary?.contains(event.target)) {
      if (details.open) {
        // Horizon's toggle handler closes the drawer with its transition; skip the native
        // <details> toggle, which would hide the drawer at once
        event.preventDefault();
      } else {
        placeDrawer(details);
      }

      return;
    }

    if (details.open && event.target.closest('#header-component') && !details.contains(event.target)) {
      removeTrapFocus();

      /** @type {(HTMLElement & { close?: () => void }) | null} */
      const headerDrawer = details.closest('header-drawer');
      headerDrawer?.close?.();
    }
  },
  { capture: true }
);

// Fallback for drawers opened without a click on the menu icon
document.addEventListener(
  'toggle',
  (event) => {
    const details = getDetails();

    if (details && event.target === details && details.open) placeDrawer(details);
  },
  { capture: true }
);

// Orientation changes and resizes: measure again while open, drop the layout where it no longer applies
window.addEventListener('resize', () => {
  const details = getDetails();

  if (!details) return;

  if (details.open) {
    placeDrawer(details);
  } else if (!opensBelowHeader(details)) {
    details.removeAttribute('data-ms-below-header');
  }
});
